import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  CheckCircle, AlertTriangle, Loader2, RotateCw, ThumbsUp, X, Send,
  Package, FileText, Upload, Paperclip, Calendar, DollarSign, Clock,
  MessageCircle, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUR_RATE = 0.92;
const fmtAmount = (usd, currency) => currency === "eur" ? `€${(usd * EUR_RATE).toFixed(0)}` : `$${usd?.toFixed(0)}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

const PAID_STATUSES = new Set([
  "pending", "ai_magicians", "human_review", "pending_feedback",
  "applying_feedback", "final_review", "delivered",
]);
const ACCEPTANCE_STATUSES = new Set(["final_review", "delivered"]);

// ------------------------------------------------------------------
// Delivery acceptance
// ------------------------------------------------------------------

function DeliveryAcceptanceCard({ project, email, onChange }) {
  const [openAccept, setOpenAccept] = useState(false);
  const [openRevision, setOpenRevision] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!ACCEPTANCE_STATUSES.has(project.status) || project.delivery_accepted_at) {
    if (project.delivery_accepted_at) {
      return (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-3" data-testid="delivery-accepted-banner">
          <CheckCircle size={18} className="text-emerald-700 mt-0.5" />
          <div>
            <div className="font-bold text-emerald-900 text-sm">Delivery accepted on {fmtDate(project.delivery_accepted_at)}</div>
            <p className="text-xs text-emerald-800/80 mt-1">Thanks for confirming. Your IP transfer is complete once final payment clears (if it hasn't already).</p>
          </div>
        </div>
      );
    }
    return null;
  }

  const accept = async () => {
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/projects/${project.project_id}/accept-delivery`, {
        email, notes: notes.trim() || undefined,
      });
      setOpenAccept(false); setNotes("");
      onChange?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not record acceptance");
    } finally { setSubmitting(false); }
  };

  const revise = async () => {
    setSubmitting(true); setError("");
    if (notes.trim().length < 10) {
      setError("Please describe what you'd like us to revise (at least 10 characters).");
      setSubmitting(false); return;
    }
    try {
      await axios.post(`${API}/projects/${project.project_id}/request-revision`, {
        email, notes: notes.trim(),
      });
      setOpenRevision(false); setNotes("");
      onChange?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not submit revision request");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-gradient-to-br from-emerald-50 via-white to-blue-50 border border-emerald-200 rounded-2xl p-5" data-testid="delivery-acceptance-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0">
          <Package size={18} className="text-emerald-700" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-zinc-950 text-base">Your project is in final review</h3>
          <p className="text-sm text-zinc-600 mt-1">Happy with the delivery? Accept it to confirm the handover, or tell us what still needs work.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={() => { setOpenAccept(true); setError(""); setNotes(""); }} data-testid="btn-open-accept-delivery"
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-full hover:bg-emerald-700">
              <ThumbsUp size={14} /> Accept delivery
            </button>
            <button onClick={() => { setOpenRevision(true); setError(""); setNotes(""); }} data-testid="btn-open-request-revision"
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-white border border-zinc-300 text-zinc-800 text-sm font-semibold rounded-full hover:bg-zinc-50">
              <RotateCw size={14} /> Request revisions
            </button>
          </div>
        </div>
      </div>

      {openAccept && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={() => !submitting && setOpenAccept(false)} data-testid="modal-accept-delivery">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3"><CheckCircle size={18} className="text-emerald-700" /><h3 className="font-bold text-zinc-950">Accept this delivery</h3></div>
            <p className="text-sm text-zinc-600 mb-3">Confirming acceptance closes the project. Any additional work from here will go through the Change Request flow below.</p>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="(Optional) Anything you'd like to note for the record"
              data-testid="input-accept-notes"
              className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-2" data-testid="accept-error">{error}</div>}
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setOpenAccept(false)} disabled={submitting} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-100 rounded-full">Cancel</button>
              <button onClick={accept} disabled={submitting} data-testid="btn-confirm-accept"
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-full hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2">
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Recording…</> : <>Yes, accept <ChevronRight size={14} /></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {openRevision && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={() => !submitting && setOpenRevision(false)} data-testid="modal-request-revision">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3"><RotateCw size={18} /><h3 className="font-bold text-zinc-950">Request revisions</h3></div>
            <p className="text-sm text-zinc-600 mb-3">Tell us specifically what you'd like revised. We'll pause delivery and come back to you within a business day.</p>
            <textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. The checkout button is off-center on mobile. The welcome email needs new copy (attached in chat)."
              data-testid="input-revision-notes"
              className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-2" data-testid="revision-error">{error}</div>}
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setOpenRevision(false)} disabled={submitting} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-100 rounded-full">Cancel</button>
              <button onClick={revise} disabled={submitting} data-testid="btn-confirm-revision"
                className="px-5 py-2 bg-zinc-950 text-white text-sm font-semibold rounded-full hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2">
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <>Submit request <Send size={14} /></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Change requests (post-payment scope changes)
// ------------------------------------------------------------------

function ChangeRequestsCard({ project, email, onChange }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${project.project_id}/change-requests`, { params: { email } });
      setRequests(res.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.project_id]);

  if (!PAID_STATUSES.has(project.status)) return null;

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (form.title.trim().length < 3) { setError("Please provide a short title"); return; }
    if (form.description.trim().length < 10) { setError("Please describe the change in a little more detail"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/projects/${project.project_id}/change-requests`, { email, ...form });
      setOpenNew(false); setForm({ title: "", description: "" });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(false); }
  };

  const approveAndPay = async (r) => {
    setProcessing(r.request_id);
    try {
      const res = await axios.post(
        `${API}/projects/${project.project_id}/change-requests/${r.request_id}/approve`,
        { email, origin_url: window.location.origin },
      );
      window.location.href = res.data.checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Could not approve");
      setProcessing(null);
    }
  };

  const decline = async (r) => {
    const reason = window.prompt("Any reason to share with the team? (Optional)", "");
    if (reason === null) return;
    setProcessing(r.request_id);
    try {
      await axios.post(`${API}/projects/${project.project_id}/change-requests/${r.request_id}/decline`, { email, reason: reason || undefined });
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Could not decline"); }
    finally { setProcessing(null); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="change-requests-card">
      <div className="flex items-start gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1">
          <MessageCircle size={16} className="text-zinc-700" />
          <h3 className="font-bold text-zinc-950 text-sm">Change requests</h3>
          {requests.length > 0 && <span className="text-[10px] font-bold bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-full">{requests.length}</span>}
        </div>
        <button onClick={() => { setOpenNew(true); setError(""); }} data-testid="btn-open-change-request"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800">
          <Send size={11} /> Submit change
        </button>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Need something added, modified, or removed mid-project? Submit a request — the team will respond with a cost + timeline. You approve before anything gets billed.</p>

      {loading ? <div className="py-4 flex justify-center"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>
        : requests.length === 0 ? <p className="text-sm text-zinc-500 py-3 italic">No change requests yet.</p>
        : (
          <div className="space-y-3" data-testid="change-requests-list">
            {requests.map((r) => (
              <div key={r.request_id} data-testid={`change-request-${r.request_id}`}
                className={`rounded-xl border p-3 ${
                  r.status === "approved" ? "bg-emerald-50 border-emerald-200" :
                  r.status === "declined" ? "bg-zinc-50 border-zinc-200 opacity-75" :
                  r.status === "quoted" ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"
                }`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-bold text-zinc-950 text-sm flex-1 min-w-0 truncate">{r.title}</span>
                  <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    r.status === "approved" ? "bg-emerald-600 text-white" :
                    r.status === "declined" ? "bg-zinc-400 text-white" :
                    r.status === "quoted" ? "bg-amber-500 text-white" : "bg-blue-500 text-white"
                  }`}>{r.status.replace("_", " ")}</span>
                </div>
                <p className="text-xs text-zinc-700 whitespace-pre-wrap mb-2">{r.description}</p>
                {r.admin_response && (
                  <div className="mt-2 border-t pt-2 border-zinc-200/60">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Our response</div>
                    <p className="text-xs text-zinc-800 whitespace-pre-wrap">{r.admin_response}</p>
                    {r.estimated_amount_usd != null && (
                      <div className="mt-2 flex items-center gap-3 text-xs">
                        <span className="inline-flex items-center gap-1 font-bold text-zinc-950"><DollarSign size={11} />{fmtAmount(r.estimated_amount_usd, r.estimated_currency)}</span>
                        {r.estimated_delivery_days && <span className="inline-flex items-center gap-1 text-zinc-600"><Clock size={11} />~{r.estimated_delivery_days} day(s) added</span>}
                      </div>
                    )}
                  </div>
                )}
                {r.status === "quoted" && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => approveAndPay(r)} disabled={processing === r.request_id} data-testid={`btn-approve-cr-${r.request_id}`}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-full hover:bg-emerald-700 disabled:opacity-50">
                      {processing === r.request_id ? <Loader2 size={11} className="animate-spin" /> : <ThumbsUp size={11} />}
                      Approve & pay
                    </button>
                    <button onClick={() => decline(r)} disabled={processing === r.request_id} data-testid={`btn-decline-cr-${r.request_id}`}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-white border border-zinc-300 text-zinc-700 text-xs font-semibold rounded-full hover:bg-zinc-50 disabled:opacity-50">
                      <X size={11} /> Decline
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

      {openNew && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={() => !submitting && setOpenNew(false)} data-testid="modal-new-change-request">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-zinc-950 text-base mb-3">Submit a change request</h3>
            <form onSubmit={submit} className="space-y-3">
              <input required data-testid="input-cr-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Short title (e.g. 'Add team invites')"
                className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
              <textarea required rows={5} data-testid="input-cr-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe what you'd like added / changed / removed. Include any constraints or links."
                className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
              {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="cr-error">{error}</div>}
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setOpenNew(false)} disabled={submitting} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-100 rounded-full">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="btn-submit-cr"
                  className="px-5 py-2 bg-zinc-950 text-white text-sm font-semibold rounded-full hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2">
                  {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <>Send request <Send size={14} /></>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Client asset uploads (mid-delivery)
// ------------------------------------------------------------------

function ClientAssetsCard({ project, email }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [description, setDescription] = useState("");
  const fileRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${project.project_id}/files`, { params: { email } });
      const onlyAssets = (res.data || []).filter((f) => f.category === "client_asset");
      setAssets(onlyAssets);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.project_id]);

  if (!PAID_STATUSES.has(project.status)) return null;

  const onPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!/\.(zip|pdf)$/i.test(f.name)) { setError("Only .zip or .pdf files are allowed."); e.target.value = ""; return; }
    if (f.size > 25 * 1024 * 1024) { setError("File too large (max 25 MB)."); e.target.value = ""; return; }
    setPendingFile(f); setError("");
  };

  const upload = async () => {
    if (!pendingFile) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("email", email);
      fd.append("file", pendingFile);
      fd.append("category", "client_asset");
      if (description.trim()) fd.append("description", description.trim());
      await axios.post(`${API}/projects/${project.project_id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingFile(null); setDescription("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) { setError(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const download = async (f) => {
    try {
      const res = await axios.get(`${API}/projects/${project.project_id}/files/${f.file_id}/download`, { params: { email }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = f.filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Download failed"); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="client-assets-card">
      <div className="flex items-center gap-2 mb-2">
        <Upload size={16} className="text-zinc-700" />
        <h3 className="font-bold text-zinc-950 text-sm">Project assets</h3>
        {assets.length > 0 && <span className="text-[10px] font-bold bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-full">{assets.length}</span>}
      </div>
      <p className="text-xs text-zinc-500 mb-3">Share brand assets, design specs, or reference material with the team (.pdf or .zip, max 25 MB). Separate from chat attachments so they don't get buried.</p>

      <div className="flex items-center gap-2 flex-wrap">
        <input ref={fileRef} type="file" accept=".pdf,.zip" onChange={onPick} className="hidden" data-testid="input-client-asset" />
        <button onClick={() => fileRef.current?.click()} data-testid="btn-pick-client-asset"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-100 text-zinc-700 text-xs font-semibold rounded-full hover:bg-zinc-200">
          <Paperclip size={11} /> Choose file
        </button>
        {pendingFile && <span className="text-xs text-zinc-600 truncate max-w-[14rem]">{pendingFile.name}</span>}
      </div>
      {pendingFile && (
        <div className="mt-3 space-y-2">
          <input data-testid="input-client-asset-desc" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description (optional)"
            className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-2">
            <button onClick={upload} disabled={uploading} data-testid="btn-upload-client-asset"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800 disabled:opacity-50">
              {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Upload
            </button>
            <button onClick={() => { setPendingFile(null); setDescription(""); setError(""); if (fileRef.current) fileRef.current.value = ""; }} className="px-3 py-2 text-zinc-600 text-xs font-semibold rounded-full hover:bg-zinc-100">Cancel</button>
          </div>
        </div>
      )}
      {!pendingFile && error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-2">{error}</div>}

      {loading ? <div className="py-3 flex justify-center"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>
        : assets.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="client-assets-list">
            {assets.map((f) => (
              <div key={f.file_id} data-testid={`client-asset-${f.file_id}`}
                className="flex items-center justify-between gap-2 p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-zinc-900 text-xs truncate inline-flex items-center gap-1"><FileText size={11} /> {f.filename}</div>
                  {f.description && <div className="text-[11px] text-zinc-500 mt-0.5">{f.description}</div>}
                  <div className="text-[10px] text-zinc-400 mt-0.5">{Math.round(f.size_bytes / 1024)} KB · {fmtDate(f.created_at)}</div>
                </div>
                <button onClick={() => download(f)} data-testid={`btn-download-client-asset-${f.file_id}`}
                  className="p-1.5 text-zinc-700 hover:bg-zinc-100 rounded-lg" title="Download">
                  <ChevronRight size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ------------------------------------------------------------------
// Public export: everything wrapped
// ------------------------------------------------------------------

export default function ClientWorkflows({ project, email, onChange }) {
  if (!email || !project) return null;
  return (
    <div className="space-y-5">
      <DeliveryAcceptanceCard project={project} email={email} onChange={onChange} />
      <ChangeRequestsCard project={project} email={email} onChange={onChange} />
      <ClientAssetsCard project={project} email={email} />
    </div>
  );
}
